"use client";

import { useCallback, useEffect, useState } from "react";
import { Check, Copy, ExternalLink, Loader2, LockKeyhole, Radio } from "lucide-react";

import { showToast } from "@/app/_module/utils/show-toast";
import {
  getAgencyTvSettings,
  lockAgencyTvSessions,
  updateAgencyTvSettings,
  type AgencyTvSettings,
} from "../../services/agency-tv";

interface AgencyTvDisplaySettingsProps {
  agencyId: string;
}

function messageFrom(error: unknown, fallback: string) {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return fallback;
}

export default function AgencyTvDisplaySettings({ agencyId }: AgencyTvDisplaySettingsProps) {
  const [settings, setSettings] = useState<AgencyTvSettings | null>(null);
  // The code the server made when it created this display. Shown once; gone on the next load.
  const [freshPin, setFreshPin] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!agencyId) return;
    setIsLoading(true);
    try {
      const next = await getAgencyTvSettings(agencyId);
      setSettings(next);
      if (next.newPin) setFreshPin(next.newPin);
    } catch (error) {
      showToast(messageFrom(error, "Could not load TV Display settings"), "error");
    } finally {
      setIsLoading(false);
    }
  }, [agencyId]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const saveSettings = async (input: { enabled?: boolean; pin?: string }) => {
    setIsSaving(true);
    try {
      const next = await updateAgencyTvSettings(agencyId, input);
      setSettings(next);
      setPin("");
      if (input.pin) setFreshPin(null);
      showToast(input.pin ? "Screen code updated" : next.enabled ? "TV Display is on" : "TV Display is off", "success");
    } catch (error) {
      showToast(messageFrom(error, "Could not update TV Display"), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async () => {
    if (!settings) return;
    if (!settings.enabled && !settings.hasPin) {
      showToast("Set a six-digit screen code first", "error");
      return;
    }
    await saveSettings({ enabled: !settings.enabled });
  };

  const handlePinSave = async () => {
    const normalizedPin = pin.replace(/\D/g, "");
    if (normalizedPin.length !== 6) {
      showToast("Use a six-digit numeric screen code", "error");
      return;
    }
    await saveSettings({ pin: normalizedPin, enabled: settings?.enabled ?? true });
  };

  const handleCopy = async () => {
    if (!settings?.displayUrl) return;
    try {
      await navigator.clipboard.writeText(settings.displayUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      showToast("Copy the address from the field", "error");
    }
  };

  const handleLockAll = async () => {
    if (!settings || !window.confirm("Lock every connected TV now? Each screen will need the six-digit code again.")) return;
    setIsLocking(true);
    try {
      await lockAgencyTvSessions(agencyId);
      showToast("Every TV screen has been locked", "success");
    } catch (error) {
      showToast(messageFrom(error, "Could not lock TV screens"), "error");
    } finally {
      setIsLocking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-56 items-center justify-center" aria-live="polite">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="ml-2 text-sm text-gray-500">Loading TV Display</span>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 px-5 py-10 text-sm text-gray-500">
        TV Display settings are unavailable right now. Try again in a moment.
      </div>
    );
  }

  const on = settings.enabled;

  return (
    <section className="max-w-3xl">
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
          <Radio className="h-3.5 w-3.5" />
          Agency TV
        </div>
        <h2 className="text-xl font-semibold tracking-tight text-gray-950">TV Display</h2>
        <p className="mt-1 max-w-xl text-sm leading-6 text-gray-500">
          A live production wall for the office screen: top producers, the bell, and your agency with everyone under it. No client details.
        </p>
      </div>

      <div className="grid gap-4">
        {/* The switch is the whole point of this page, so it is the first thing and the biggest thing. */}
        <div className={`rounded-xl border p-5 transition-colors ${on ? "border-primary/30 bg-primary/[0.06]" : "border-gray-200 bg-white"}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className={`h-3 w-3 rounded-full ${on ? "bg-primary shadow-[0_0_0_4px_rgba(37,99,235,.15)]" : "bg-gray-300"}`} aria-hidden="true" />
              <div>
                <p className="text-base font-semibold text-gray-950">{on ? "Display is on" : "Display is off"}</p>
                <p className="text-sm text-gray-500">{on ? "Any screen with the address and the code can show the wall." : "Screens that open the address see a turned-off notice."}</p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label={on ? "Turn TV Display off" : "Turn TV Display on"}
              disabled={isSaving}
              onClick={() => void handleToggle()}
              className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-60 ${on ? "bg-primary" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${on ? "translate-x-7" : "translate-x-1"}`} aria-hidden="true" />
            </button>
          </div>

          {settings.displayUrl && (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <div className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 font-mono text-sm text-gray-700">
                <span className="block truncate">{settings.displayUrl}</span>
              </div>
              <button
                type="button"
                onClick={() => void handleCopy()}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
              <a
                href={settings.displayUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <ExternalLink className="h-4 w-4" />
                Open
              </a>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="font-semibold text-gray-900">Screen code</h3>
          <p className="mt-0.5 text-sm leading-5 text-gray-500">Six digits, typed once on each TV. Changing it locks every connected screen.</p>

          {freshPin && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-800">Your screen code</p>
              <p className="mt-1 font-mono text-3xl font-bold tracking-[0.3em] text-amber-950">{freshPin}</p>
              <p className="mt-1 text-sm text-amber-900">Write it down. It is shown only this once; you can set a new one below any time.</p>
            </div>
          )}

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block flex-1">
              <span className="mb-1.5 block text-sm font-medium text-gray-700">{settings.hasPin ? "New code" : "Code"}</span>
              <input
                value={pin}
                onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                maxLength={6}
                placeholder="••••••"
                className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 font-mono text-base tracking-[0.28em] text-gray-950 outline-none transition-shadow placeholder:tracking-[0.28em] focus:border-primary focus:ring-2 focus:ring-primary/15"
              />
            </label>
            <button
              type="button"
              onClick={() => void handlePinSave()}
              disabled={isSaving || pin.length !== 6}
              className="inline-flex min-h-11 items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-45"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : settings.hasPin ? "Change code" : "Set code"}
            </button>
          </div>
        </div>

        {settings.hasPin && (
          <div className="flex flex-col justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50/70 p-5 sm:flex-row sm:items-center">
            <div className="flex gap-3">
              <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-gray-700" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Lock every screen</h3>
                <p className="mt-0.5 text-sm leading-5 text-gray-500">If a TV is moved, replaced, or left somewhere it should not be.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void handleLockAll()}
              disabled={isLocking}
              className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-800 transition-colors hover:border-gray-400 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLocking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Lock all screens"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
